import mixpanel from "mixpanel-browser";
import { isDevEnv, isLocalEnv, isAutomationEnv, getEnvironmentType } from "../environment";

// NOTE: those selectors will be masked in Mixpanel session reply,
// for any PHI data we can use data-mp-sensitive on the element
// or add the selector to the following list
export const MASK_TEXT_SELECTORS = [
  "#patient_name",
  "#patient_name_header",
  "#patient_name_tasks_popover_title",

  "#patient_id",
  "#patient_id_header",
  "#patient_id_and_mrn",

  "#patient_mrn",
  "#patient_mrn_header",
  "#ManagePatientDetails_mrn",

  "#patient_dob",
  "#patient_dob_header",

  "#patient_ssn",
  "#ManagePatientDetails_socialSecurityNumber",

  "#patient_phone_number",
  "#patient_phone_number_header",
  "#contact_details_phone_number",
  "#alternate_contact_phone_number",
  "#comm-settings-sms-phone-text",

  "#alternate_contact_name",
  "#alternate_contact_relation_to_patient",
  "#alternate_contact_email",
  "#comm-settings-email-text",

  "#patient_message_preview",
  "#task-description",

  "#track_form_item_input_application_id",
  "#track_display_text_application_id",
  "#track_form_item_input_member_id",
  "#track_display_text_member_id",

  "#track_form_item_input_copay_card",
  "#track_display_text_copay_card",
  "#track_form_item_input_cvv",
  "#track_display_text_cvv",

  "#track_reason_field",
  "#track_display_text_reason_field",

  "#signed_by_name",

  // Dynamic IDs used for signer contact info (e-sign, and self-enroll)
  '[id*="phone"]',
  '[id*="email"]'
];
const MIXPANEL_INIT_OPTIONS = {
  debug: isDevEnv(),
  autocapture: false,
  track_pageview: false,
  record_sessions_percent: isDevEnv() ? 0 : 100,
  record_console: false,
  record_heatmap_data: !isDevEnv(),
  record_mask_text_selector: MASK_TEXT_SELECTORS.join(", "),
  // Discard session replays shorter than 6 seconds to reduce recorded-session volume
  record_min_ms: 6000,
  persistence: "localStorage"
};
const MIXPANEL_CLIENT_GROUP_KEY = "account";
const MIXPANEL_EXPERIMENT_EXPOSURE_EVENT_NAME = "$experiment_started";

export const MIXPANEL_EXPERIMENT_NAME_PROP = "Experiment name";
export const MIXPANEL_EXPERIMENT_VARIANT_PROP = "Variant name";
export const MIXPANEL_DEFAULT_VARIANT_FLAG_FALLBACK = "control";

export const MIXPANEL_FLAG_TYPE = Object.freeze({
  BOOLEAN: "boolean",
  VARIANT: "variant"
});

export const buildMixpanelDistinctId = ({ personaType, personaId, client }) =>
  `tm_${personaType}_${personaId}_${client}`;

// First-party proxies (adblocker bypass). The project token rides in the payload,
// so the single prod host serves every non-dev env (staging/sandbox/demo/prod).
const MIXPANEL_DEV_PROXY_API_HOST = "https://mp.dev.tailormed.co";
const MIXPANEL_PROD_PROXY_API_HOST = "https://mp.tailormed.co";
// Opaque endpoint paths (adblock lists match /track etc.); the proxy rewrites them back.
// /decide + /flags aren't configurable via api_routes and stay on default paths (not billing).
const MIXPANEL_PROXY_API_ROUTES = {
  track: "e/a/",
  engage: "e/g/",
  groups: "e/gr/",
  record: "e/r/"
};

const getMixpanelProxyApiHost = () =>
  getEnvironmentType() === "dev" ? MIXPANEL_DEV_PROXY_API_HOST : MIXPANEL_PROD_PROXY_API_HOST;

export const initMixpanel = (extraOptions = {}) => {
  const token = process.env.REACT_APP_MIXPANEL_PROJECT_TOKEN;
  if (!token || isLocalEnv || isAutomationEnv) return;
  try {
    mixpanel.init(token, {
      api_host: getMixpanelProxyApiHost(),
      api_routes: MIXPANEL_PROXY_API_ROUTES,
      ...MIXPANEL_INIT_OPTIONS,
      ...extraOptions
    });
    if (isDevEnv()) {
      // Evict any replay the SDK may have resumed from its IndexedDB
      // recording_registry — registry entries can carry recordings across
      // loads even when record_sessions_percent is 0.
      mixpanel.stop_session_recording();
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[Mixpanel] init failed", error);
  }
};

export const trackMixpanelEvent = (eventName, properties = {}) => {
  try {
    if (isLocalEnv || isAutomationEnv) return;
    mixpanel.track(eventName, properties);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[Mixpanel] track failed", error);
  }
};

export const identifyMixpanelUser = (user, client) => {
  if (!user?.id || !client || isLocalEnv || isAutomationEnv) return;

  const distinctId = buildMixpanelDistinctId({
    personaType: "user",
    personaId: user.id,
    client
  });

  try {
    mixpanel.identify(distinctId);
    mixpanel.set_group(MIXPANEL_CLIENT_GROUP_KEY, client);
    mixpanel.people.set({
      client,
      userId: user.id,
      $email: user.email,
      $first_name: user.firstName,
      $last_name: user.lastName,
      role: user.role
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[Mixpanel] identify failed", error);
  }
};
export const getMixpanelFeatureFlagValue = (flagKey, flagType = MIXPANEL_FLAG_TYPE.VARIANT) => {
  try {
    if (flagType === MIXPANEL_FLAG_TYPE.VARIANT) {
      return mixpanel.flags.get_variant_value_sync(flagKey, MIXPANEL_DEFAULT_VARIANT_FLAG_FALLBACK);
    }

    if (flagType === MIXPANEL_FLAG_TYPE.BOOLEAN) {
      return mixpanel.flags.is_enabled_sync(flagKey, false);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`[Mixpanel] Fetching flag failed ${flagKey}`, error);
  }

  return null;
};

export const trackMixpanelExperimentStarted = (experimentName, variantName, extraProps = {}) => {
  try {
    mixpanel.track(MIXPANEL_EXPERIMENT_EXPOSURE_EVENT_NAME, {
      [MIXPANEL_EXPERIMENT_NAME_PROP]: experimentName,
      [MIXPANEL_EXPERIMENT_VARIANT_PROP]: variantName,
      ...extraProps
    });
    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[Mixpanel] experiment exposure track failed", error);
    return false;
  }
};

export const resetMixpanelUser = () => {
  try {
    mixpanel.reset();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[Mixpanel] reset failed", error);
  }
};
